import { AppstoreAddOutlined, HolderOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useState } from 'react';
import { useDragOperation, useDroppable } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { pointerIntersection } from "@dnd-kit/collision";
import Icon, { ICON_NAME } from "../../../components/Icon";
import Footer from "../../../components/Popup/_components/Footer";
import { openPopup2 } from "../../../contexts/PopupContext";
import { ChecklistService } from "../../../util/services/checklist";
import { showErrorResponse } from "../../../util/varibles/global";
import { IChecklist, IPropsChildren } from "../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../util/varibles/message";
import Assignments from "./Assignments";
import { callEventChecklistEdit, getChecklistEdit, setChecklistEdit, useChecklistEdit } from "./ChecklistEditContext";
import MiddleElement, { ElementPreview } from "./MiddleElement";
import { ELEMENT_ID, initPropertiesElement, IPropsElement } from "./constants";
import styles from "./style.module.scss";
import { isRedundantRowSlot } from "./dragDrop";

const Name = () => {
    const [name, set] = useChecklistEdit(state => state.data?.name);

    return <input
        className={styles.name}
        value={name || ""}
        onChange={({ target }) => set(({ data: prev }) => ({
            data: { ...prev, name: (target.value || "") }
        }))}
        placeholder="Checklist name"
    />
}

const Assign = () => {
    const get = getChecklistEdit();
    const set = setChecklistEdit();

    const onClick = () => {
        const { data } = get();
        openPopup2(<Assignments
            data={data}
            onSave={required_map => set(({ data }) => ({ data: { ...data, required_map } }))}
        />, {
            modalProps: { title: "Assignments" },
        });
    }

    return <Button className='bt-ghost px-2 min-w-[140px!important]' data-icon={true} onClick={onClick}>
        <span>Assign checklist</span>
        <AppstoreAddOutlined />
    </Button>
}

const PaletteSlot = ({ rowIndex, position, count }: { rowIndex: number, position: number, count: number }) => {
    const { source } = useDragOperation();
    const isDivide = source?.data.element?.type === ELEMENT_ID.DIVIDE || source?.data.elementType === ELEMENT_ID.DIVIDE;
    const { ref, isDropTarget } = useDroppable({
        id: `palette-slot-${rowIndex}-${position}`,
        collisionDetector: pointerIntersection,
        accept: source => source.type === 'checklist-new-element'
            || (source.type === 'checklist-element' && source.data.element.type === ELEMENT_ID.DIVIDE),
        collisionPriority: 2,
        data: { rowIndex, position, insertionSlot: true },
    });
    return <div ref={ref} className="relative h-full flex-1">
        {isDivide && isDropTarget && <div className={`absolute inset-y-1 w-[24px] rounded border-2 border-dashed border-[var(--colorActive)] bg-[var(--colorHover)] ${position === 0 ? 'left-0' : position === count ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}>
            <div className="absolute inset-y-2 left-1/2 w-px bg-[var(--colorActive)]" />
        </div>}
    </div>;
};

const NewElementPlaceholder = () => {
    const { source } = useDragOperation();
    if (source?.type !== 'checklist-new-element') return null;
    const verticalDivide = source.data.elementType === ELEMENT_ID.DIVIDE;
    return <div className={styles.newElementPlaceholder} data-vertical-divide={verticalDivide} aria-hidden="true">
        <div className={`${styles.element} h-full !border-dashed !border-[var(--colorActive)] opacity-50`} data-vertical-divide={verticalDivide}>
            <ElementPreview data={initPropertiesElement(source.data.elementType)} />
        </div>
    </div>;
};

const Row: React.FC<{ index: number, count: number } & IPropsChildren> = ({ children, index, count }) => {
    const { source } = useDragOperation();
    const isExistingDivide = source?.type === 'checklist-element' && source.data.element.type === ELEMENT_ID.DIVIDE;
    const { onRowDelete } = callEventChecklistEdit();
    const { ref: dragRef, handleRef, isDragging } = useSortable({
        id: `row-${index}`,
        collisionDetector: pointerIntersection,
        index,
        group: 'checklist-rows',
        accept: 'checklist-row',
        transition: { duration: 250 },
        type: 'checklist-row',
        data: { rowIndex: index },
    });
    const { ref: dropRef, isDropTarget } = useDroppable({
        id: `row-target-${index}`,
        collisionDetector: pointerIntersection,
        accept: ['checklist-element', 'checklist-new-element'],
        collisionPriority: -1,
        data: { rowIndex: index },
    });

    return <div ref={element => { dragRef(element); dropRef(element); }}
        data-checklist-row={index}
        className={`${styles.row} ${isDragging ? 'opacity-40' : ''} ${isDropTarget ? 'outline outline-2 outline-[color:var(--colorActive)]' : ''}`}
        data-hover={isDropTarget}
        data-selected={false}
    >
        {children}
        {(source?.type === 'checklist-new-element' || isExistingDivide) && <div className="absolute inset-0 z-[5] flex pointer-events-none">
            {Array.from({ length: count + 1 }, (_, position) => position)
                .filter(position => !(isExistingDivide && source?.data.rowIndex === index && position === source.data.position + 1))
                .map(position => <PaletteSlot key={position} rowIndex={index} position={position} count={count} />)}
        </div>}
        <div className={styles.rowControl}>
            <div ref={handleRef} className="flex items-center gap-1 cursor-ns-resize touch-none"
                onMouseDown={event => event.stopPropagation()}
            >
                <HolderOutlined />
                <span>Row {index + 1}</span>
            </div>
            <div data-role="delete" onClick={() => onRowDelete(index)}>
                <Icon className='cursor-pointer' icon={ICON_NAME.DELETE} danger />
            </div>
        </div>
    </div>;
};

const NewRowSlot = ({ index }: { index: number }) => {
    const { source } = useDragOperation();
    const [dragging] = useChecklistEdit(state => state.dragging);
    const [redundant] = useChecklistEdit(state => isRedundantRowSlot(
        state.form, source?.type === 'checklist-element' ? source.data.rowIndex : undefined, index
    ));
    const isElementDragging = dragging !== undefined && !redundant && (source?.type === 'checklist-element' || source?.type === 'checklist-new-element');
    const { ref, isDropTarget } = useDroppable({
        id: `new-row-before-${index}`,
        collisionDetector: pointerIntersection,
        collisionPriority: 3,
        accept: ['checklist-element', 'checklist-new-element'],
        data: { rowIndex: index, position: 0, newRow: true },
        disabled: !isElementDragging,
    });

    return <div ref={ref} className={`relative shrink-0 rounded ${isElementDragging ? 'h-[28px]' : 'h-0'} ${isDropTarget ? 'bg-[var(--colorHover)]' : ''}`}>
        {isElementDragging && <>
            <div className={`absolute inset-x-0 top-1/2 border-t-2 border-dashed border-[var(--colorActive)] ${isDropTarget ? '' : 'opacity-30'}`} />
            {isDropTarget && <span className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-[var(--bgSection)] px-2 py-1 text-xs leading-[16px] font-semibold text-[var(--colorActive)] whitespace-nowrap pointer-events-none">
                + New row
            </span>}
        </>}
    </div>;
};

const Last: React.FC<{ index: number }> = ({ index }) => {
    const { source } = useDragOperation();
    const [redundant] = useChecklistEdit(state => isRedundantRowSlot(
        state.form, source?.type === 'checklist-element' ? source.data.rowIndex : undefined, index
    ));
    const [dragging] = useChecklistEdit(state => state.dragging);
    const { ref, isDropTarget } = useDroppable({
        id: 'checklist-new-row',
        collisionDetector: pointerIntersection,
        collisionPriority: 3,
        accept: ['checklist-element', 'checklist-new-element'],
        data: { rowIndex: index, position: 0, newRow: true },
        disabled: redundant,
    });

    return <div ref={ref} className={`${styles.row} min-h-[60px] !outline-none ${dragging !== undefined && !redundant ? '!border-2 !border-dashed !border-[var(--colorActive)] !rounded-md' : ''} ${isDropTarget ? 'bg-[var(--colorHover)]' : ''}`}
        data-checklist-row={index}
        data-hover={isDropTarget} data-selected={false}
    >
        <div className={styles.empty} data-show={!redundant && (!index || dragging !== undefined)}>
            {index ? <p>Drop here to create a new row.</p> : <>
                <p>This checklist is currently empty.</p>
                <p>Click or drag a component from the left banner here to create new one.</p>
            </>}
        </div>
    </div>;
};

const Elements: React.FC = () => {
    const [form] = useChecklistEdit(state => state.form);
    const [preview] = useChecklistEdit(state => state.palettePreview);

    return <div className={styles.content} data-checklist-form>
        {form.map((item, index) => <React.Fragment key={index}>
            <NewRowSlot index={index} />
            <Row index={index} count={item.length}>
                {item.map((sub, position) => <React.Fragment key={sub.index}>
                    {preview?.rowIndex === index && preview.position === position && <NewElementPlaceholder />}
                    <MiddleElement rowIndex={index} position={position} data={sub} />
                </React.Fragment>)}
                {preview?.rowIndex === index && preview.position === item.length && <NewElementPlaceholder />}
            </Row>
        </React.Fragment>)}
        <Last index={form.length} />
    </div>;
};


const MiddleControl: React.FC<{ onSave(data: IChecklist): void, onClose?(): void }> = ({ onSave, onClose }) => {
    const [loading, setLoading] = useState(false)
    const get = getChecklistEdit();
    const set = setChecklistEdit();

    const handleSave = () => {
        const { data, form } = get();
        const name = (data?.name || "").trim();
        if (name.length === 0) {
            notify.error(NotifyCode.E32)(["Checklist name"]);
            const el = document.getElementsByClassName(styles.name)[0] as HTMLInputElement;
            if (el)
                el.focus();
            return;
        }

        if (form.length === 0) {
            notify.error(NotifyCode.E47)(["Checklist form"]);
            return;
        }

        const content = form.reduce((rs: IPropsElement[], item) => [...rs, ...item], []);
        const signingRoles = new Set<string>([]);
        content.forEach(item => {
            if (item.type === ELEMENT_ID.SIGN && item.properties?.options) {
                const options = JSON.parse(item.properties.options);
                options.forEach((opt: { value: string }) => {
                    signingRoles.add(opt.value);
                });
            }
        })

        const body = { ...data, name, content: JSON.stringify(content), sign_roles: Array.from(signingRoles) } as IChecklist;
        setLoading(true);
        if (data.id) {
            ChecklistService.update(body)
                .then((rs => {
                    setLoading(false);
                    notify.success(NotifyCode.S2)([body.name]);
                    onSave(rs);
                    onClose?.();
                }))
                .catch(error => {
                    setLoading(false);
                    showErrorResponse(notify[NotifyCode.E35]([body.name]), error);
                });
        } else {
            ChecklistService.create(body)
                .then((rs => {
                    setLoading(false);
                    notify.success(NotifyCode.S1)([body.name]);
                    onSave(rs)
                    onClose?.();
                }))
                .catch(error => {
                    setLoading(false);
                    showErrorResponse(notify[NotifyCode.E22]([body.name]), error);
                });
        }
    }

    return <div
        className="w-full h-full flex flex-col pt-4 pb-3 pl-[2px]"
        onMouseDown={() => set({ selected: undefined, rowSelected: undefined })}
    >
        <div className={styles.middleHeader}>
            <Name />
            <Assign />
        </div>
        <Elements />
        <Footer ok={{ loading, click: handleSave }} cancel={{ click: () => onClose?.() }} />
    </div>
};

export default MiddleControl;
